import jwtDecode from 'jwt-decode';
import VueCookies from 'vue-cookies';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { clearAssistantSessions } from '@/utils/assistant-session';


export const TOKEN_KEY = 'atok';
export const REFRESH_TOKEN_KEY = 'artok';
const USER_FULL_NAME = 'ufn';
const USER_NAME = 'un';
export const USER_PRIVILEGE = 'up';
const USER_IS_ADMIN = 'isa';

let expiresDate = null;
let authSessionInitialized = false;

const isNativePreferencesAvailable = () => Capacitor.isNativePlatform();

const setPreference = (key, value) => {
  if (!isNativePreferencesAvailable() || value === undefined || value === null) return;
  Preferences.set({ key, value: String(value) });
};

const removePreference = (key) => {
  if (!isNativePreferencesAvailable()) return;
  Preferences.remove({ key });
};

const getPreference = async (key) => {
  if (!isNativePreferencesAvailable()) return null;
  const result = await Preferences.get({ key });
  return result.value || null;
};

const normalizeRefreshResponse = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;
  return data.token || data.accessToken || data.atok || null;
};

export const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return true;
    return decoded.exp * 1000 > Date.now();
  } catch (error) {
    return false;
  }
};

const restoreCookieFromPreference = async (key, setter) => {
  if (VueCookies.get(key)) return VueCookies.get(key);

  const value = await getPreference(key);
  if (value) setter(value);
  return value;
};


export const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/signin', {
        "username": username,
        "password": password,
      },
    {
      withCredentials: true 
    });
  
      const data = response.data;
        if (data.token && data.refreshToken) {
        expiresDate = getTokenExpirationDate(data.token);
        setAuthToken(data.token);
        setRefreshToken(data.refreshToken);
        setUsername(data.username);
        setIsAdmin(data.isAdmin);
        setPrivileges(data.privileges);
        setFullName(data.fullName);
        return data;
      } else {
        throw new Error('Login failed: No tokens returned');
      }

    } catch (error) {
      this.$snotify.error('Votre nom d\'utilisateur ou mot de passe est erroné.');
      throw error;
    }
  };


export const setRefreshToken = (token) => {
    try {
      expiresDate = getTokenExpirationDate(token);
      VueCookies.set(REFRESH_TOKEN_KEY, token, expiresDate);
    } catch (error) {
      console.error('Failed to decode refresh token:', error);
      VueCookies.set(REFRESH_TOKEN_KEY, token);
    }
    setPreference(REFRESH_TOKEN_KEY, token);
  };

export const removeRefreshToken = () => {
  VueCookies.remove(REFRESH_TOKEN_KEY);
  removePreference(REFRESH_TOKEN_KEY);
};

export const isAuthenticated = () => {
  return isTokenValid(getAuthToken());
};

export const clearAuthData = async () => {
  removeAuthToken();
  removeRefreshToken();
  removePrivileges();
  removeIsAdmin();
  removeUsername();
  removeFullName();
  clearAssistantSessions();
};

export const getAuthHeaders = () => {
  const token = getAuthToken();
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }
  return {};
};


export const refreshAuthToken = async () => {
  const refreshToken = getRefreshToken();

  if (refreshToken) {
    try {
      const response = await axios.post('/api/auth/refresh-token', {
        refreshToken, 
      });

      return normalizeRefreshResponse(response.data);
    } catch (error) {
      return null;
    }
  }
  return null;
};


export const getTokenExpirationDate = (token) => {
  try {
    const decoded = jwtDecode(token);
    const expMs = decoded.exp * 1000;
    const now = Date.now();
    const durationMs = expMs - now;

    if (durationMs <= 0) {
      console.warn('Token already expired');
      return null;
    }

    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    return `${durationMinutes}min`;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};



  export const getAuthToken = () => {
  return VueCookies.get(TOKEN_KEY) || null;
};

export const setAuthToken = (token) => {
    try {
      VueCookies.set(TOKEN_KEY, token, expiresDate);
    } catch (error) {
      VueCookies.set(TOKEN_KEY, token);
    }
    setPreference(TOKEN_KEY, token);
  };

  
export const setFullName = (username) => {
    VueCookies.set(USER_FULL_NAME, username, "365d");
    setPreference(USER_FULL_NAME, username);
};
export const removeFullName = () => {
    VueCookies.remove(USER_FULL_NAME);
    removePreference(USER_FULL_NAME);
};
export const setUsername = (username) => {
    VueCookies.set(USER_NAME, username,  "365d");
    setPreference(USER_NAME, username);
};
export const removeUsername = () => {
    VueCookies.remove(USER_NAME);
    removePreference(USER_NAME);
};
export const setIsAdmin = (isAdmin) => {
    VueCookies.set(USER_IS_ADMIN, isAdmin,  "365d");
    setPreference(USER_IS_ADMIN, isAdmin);
};
export const removeIsAdmin = () => {
    VueCookies.remove(USER_IS_ADMIN);
    removePreference(USER_IS_ADMIN);
};
export const setPrivileges = (privileges) => {
    // Keep the comma-separated format: vue-cookies >= 1.8 would JSON-encode arrays.
    if (Array.isArray(privileges)) privileges = privileges.join(',');
    VueCookies.set(USER_PRIVILEGE, privileges, "365d");
    setPreference(USER_PRIVILEGE, privileges);
};
export const removePrivileges = () => {
    VueCookies.remove(USER_PRIVILEGE);
    removePreference(USER_PRIVILEGE);
};
export const removeAuthToken = () => {
  VueCookies.remove(TOKEN_KEY);
  removePreference(TOKEN_KEY);
};
export const getRefreshToken = () => {
  return VueCookies.get(REFRESH_TOKEN_KEY) || null;
};

export const restoreAuthSession = async () => {
  if (authSessionInitialized) {
    return;
  }

  await restoreCookieFromPreference(TOKEN_KEY, setAuthToken);
  await restoreCookieFromPreference(REFRESH_TOKEN_KEY, setRefreshToken);
  await restoreCookieFromPreference(USER_FULL_NAME, setFullName);
  await restoreCookieFromPreference(USER_NAME, setUsername);
  await restoreCookieFromPreference(USER_PRIVILEGE, setPrivileges);
  await restoreCookieFromPreference(USER_IS_ADMIN, setIsAdmin);

  authSessionInitialized = true;
};

export const ensureValidAuthSession = async () => {
  await restoreAuthSession();

  const accessToken = getAuthToken();
  if (isTokenValid(accessToken)) {
    return accessToken;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    await clearAuthData();
    return null;
  }

  const newToken = await refreshAuthToken();
  if (newToken && isTokenValid(newToken)) {
    expiresDate = getTokenExpirationDate(newToken);
    setAuthToken(newToken);
    return newToken;
  }

  await clearAuthData();
  return null;
};
